import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router'
import { useBeforeUnload } from 'react-use'

import { Suspense } from '~/components/elements/Suspense'
import { checkCurrentSurvey } from '~/hoc/checkCurrentSurvey'
import { useLogEventOnMount } from '~/hooks/useLogEventOnMount'
import { useSurveyQuery } from '~/hooks/useSurveyQuery'

import { commentAtom, savedCommentAtom } from './atoms'
import { OverlapAnalysis } from './components/OverlapAnalysis'
import { useResetOnUnmount } from './hooks/useResetOnUnmount'

export const OverlapAnalysisPage = checkCurrentSurvey(
  () => {
    const { t } = useTranslation()
    const { isLoading } = useSurveyQuery()
    const comment = useAtomValue(commentAtom)
    const savedComment = useAtomValue(savedCommentAtom)
    const allKeys = new Set([
      ...Object.keys(comment),
      ...Object.keys(savedComment),
    ])
    const isDirty = [...allKeys].some(
      (k) => (comment[k] ?? '') !== (savedComment[k] ?? ''),
    )
    const dirtyMessage = t(
      'edited_content_has_not_been_saved_do_you_still_want_to_leave',
    )

    const blocker = useBlocker(
      ({ currentLocation, nextLocation }) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
    )

    useBeforeUnload(isDirty, dirtyMessage)
    useLogEventOnMount('Access to overlap analysis function')
    useResetOnUnmount()

    React.useEffect(() => {
      if (blocker.state === 'blocked') {
        const isConfirmed = window.confirm(dirtyMessage)
        if (isConfirmed) {
          blocker.proceed()
        } else {
          blocker.reset()
        }
      }
    }, [blocker, dirtyMessage])

    return (
      <Suspense loading={isLoading}>
        <OverlapAnalysis />
      </Suspense>
    )
  },
  {
    checks: [
      'exists',
      ['hasMappedBrandGroupsWithBrands', { numberOfBrands: 2 }],
    ],
  },
)
